// ** React Imports
import { memo, useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useHistory, matchPath } from "react-router-dom";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { getUsers } from "store/modules/main-pages/users/get-users.action";
import { getUsersSeoContent } from "store/modules/main-pages/users/get-users-seo-content.action";
import { setUsersListViewType } from "store/modules/global-view-types.slice";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import queryString from "query-string";
import Helmet from "react-helmet";
import md5 from "md5";
import loadable from "@loadable/component";
import withHydrationOnDemand from "react-hydration-on-demand";

// ** Custom Components
import { CardWrapper } from "components/layout/Card/CardWrapper";
import { CardTitle } from "components/layout/Card/CardTitle";
import { CardBody } from "components/layout/Card/CardBody";
import { CardsBlockWrapper } from "components/layout/Card/CardsBlockWrapper";
import Heading from "components/typography/Heading";
import ScrollToTop from "components/global/ScrollToTop";
import EmptyBlock from "components/layout/Block/BlockEmpty";
import Block from "components/layout/Block";

// ** Users Components
const FiltersSection = loadable(() => import("./components/FiltersSection"));
const FiltersSectionWithHydrationOnDemand = withHydrationOnDemand({
	on: ["idle"],
	onBefore: FiltersSection.load,
})(FiltersSection);
const UsersList = loadable(() => import("./components/UsersList"));
const Breadcrumbs = loadable(() => import("./components/Breadcrumbs"));
const BreadcrumbsWithHydrationOnDemand = withHydrationOnDemand({
	on: ["idle"],
	onBefore: Breadcrumbs.load,
})(Breadcrumbs);
const NavPanelTabsSelectors = loadable(
	() => import("./components/NavPanelTabsSelectors"),
	{ ssr: false },
);

// ** Custom Hooks
import { useQueryWithLocationSearch } from "hooks/useQuery";
import useValidator from "./hooks/useValidator";
import UsersSeoNavPanelTabsSelectors from "containers/UsersSeo/components/UsersSeoNavPanelTabsSelectors";

// ** Hooks
import useUsersMetaTags from "./hooks/useUsersMetaTags";
import useUsersApiParams from "./hooks/useUsersApiParams";

// ** Utils & Helpers
import formatMessage from "utils/formatMessage";
import pseudoFormat from "utils/pseudoFormat";
import generateQuery from "utils/generateQuery";
import generateQueryPath from "utils/generateQueryPath";

// ** Config
import { initialQuery } from "./initialQuery";

// #####################################################

const LOCATIONS_WITH_SEO_CONTENT = [
	"torun",
	"lodz",
	"bochnia",
	"ciechanow",
	"legionowo",
	"warszawa",
	"sanok",
	"rumia",
	"lubliniec",
	"pszczyna",
	"raciborz",
	"sosnowiec",
	"ostrowiec-swietokrzyski",
];

const LOCATIONS_WITHOUT_SEO_PREFERENCES_PAGES = [
	"radom",
	"bytom",
	"gliwice",
	"gdynia",
	"ostrow-wielkopolski",
	"pabianice",
	"tczew",
	"sanok",
	"ciechanow",
	"krosno",
];

const LOCATIONS_WITHOUT_SSR_AND_50_RESULTS = [
	"mielec",
	"myslowice",
	"lubin",
	"nowy-sacz",
	"olsztyn",
	"rumia",
	"rybnik",
	"stalowa-wola",
	"sosnowiec",
	"zgierz",
];

const LOCATIONS_WITHOUT_SSR = [
	"belchatow",
	"chelm",
	"chorzow",
	"elk",
	"glogow",
	"kalisz",
	"kielce",
	"katowice",
	"myslowice",
	"poznan",
];

const LOCATIONS_WITHOUT_SSR_50_RESULTS = [
	"mielec",
	"myslowice",
	"lubin",
	"nowy-sacz",
	"olsztyn",
	"rumia",
	"rybnik",
	"stalowa-wola",
	"sosnowiec",
	"zgierz",
];

const LOCATIONS_WITH_H1_LIKE_TITLE = [
	"bydgoszcz",
	"czestochowa",
	"elblag",
	"jelenia-gora",
	"lodz",
	"nysa",
	"rybnik",
	"rzeszow",
	"tychy",
	"wroclaw",
];
// #####################################################

const Users = ({ match, location }) => {
	const { t } = useTranslation(["common", "pages", "filtersAndSelectors"]);

	const dispatch = useDispatch();

	const [filtersOpen, setFiltersOpen] = useState(false);

	const locationMd5HashFromState = useSelector(
		(state) => state.mainPageUsers.locationMd5Hash,
	);

	const seoContentLoading = useSelector(
		(state) => state.mainPageUsers.seoContent.loading,
	);
	const seoContentLoaded = useSelector(
		(state) => state.mainPageUsers.seoContent.loaded,
	);

	const {
		preference: preferenceUrl = null,
		preferences: preferencesUrl = null,
		country: countryUrl = null,
		region: regionUrl = null,
		city: cityUrl = null,
	} = match?.params || {};

	const [isChangingFilter, setIsChangingFilter] = useState(false);

	const usersError = useSelector((state) => state.mainPageUsers.error);

	const users = useSelector((state) => state.mainPageUsers);
	const userSearchSex = useSelector((state) => state.globalUser.searchSex);

	const history = useHistory();
	const locationPathname = location?.pathname;
	const locationSearch = location?.search;
	const notUseUserPrefferedSex = location?.state?.notUseUserSex || false;
	const forceReload = location?.state?.forceReload || false;

	const query = useQueryWithLocationSearch(locationSearch);

	const isAuth = useSelector((state) => state.globalCommon.isAuth);

	const viewType = useSelector(
		(state) => state.globalViewTypes.usersListViewType,
	);

	// ** Sprawdzenie poprawności parametrów w query
	const {
		includeAllQueryParams,
		containInvalidQueryParam,
		containInvalidPreferenceParam,
	} = useValidator({
		locationSearch,
		preferencesUrl,
	});

	const { getApiParams } = useUsersApiParams({
		viewType,
		locationSearch,
		notUseUserPrefferedSex,
		countryUrl,
		regionUrl,
		cityUrl,
		preferenceUrl,
		preferencesUrl,
	});
	const { title, metaDescription, ogMetaTags, canonicalLink } =
		useUsersMetaTags({
			locationPathname,
			preferenceUrl,
		});

	useEffect(() => {
		let currentPageScrollPosition = null;
		const path = `${locationPathname}${locationSearch}`;
		if (typeof sessionStorage !== "undefined" && sessionStorage !== null) {
			const fromSessionStorage = sessionStorage.getItem(
				"savedScrollsPositionsFromDZ",
			);

			const savedScrollsPositions = fromSessionStorage
				? JSON.parse(fromSessionStorage)
				: {};
			if (history.action === "POP") {
				currentPageScrollPosition = savedScrollsPositions[path] || null;
			}
		}

		const actualLocationMd5Hash = md5(
			`${locationPathname}${locationSearch}${isAuth ? viewType : "list"}`,
		);

		if (
			actualLocationMd5Hash !== locationMd5HashFromState &&
			(!currentPageScrollPosition || !users.data.length) &&
			!forceReload
		) {
			setIsChangingFilter(true);
			setFiltersOpen(false);
			const apiParams = getApiParams({ isAuth });

			dispatch(
				getUsers({
					apiParams,
					isAuth,
					useUrl: true,
					locationMd5Hash: actualLocationMd5Hash,
				}),
			).finally(() => {
				setIsChangingFilter(false);
			});
		}
	}, [locationPathname, locationSearch, viewType]);

	useEffect(() => {
		if (forceReload) {
			const actualLocationMd5Hash = md5(
				`${locationPathname}${locationSearch}${viewType}`,
			);

			setIsChangingFilter(true);
			const apiParams = getApiParams({ isAuth });

			dispatch(
				getUsers({
					apiParams,
					isAuth,
					useUrl: true,
					locationMd5Hash: actualLocationMd5Hash,
				}),
			).finally(() => {
				setIsChangingFilter(false);
			});

			history.replace({ ...location, state: null });
		}
	}, [forceReload]);

	useEffect(() => {
		const isLocationWithContent =
			LOCATIONS_WITH_SEO_CONTENT.includes(cityUrl);

		if (
			!seoContentLoaded &&
			!seoContentLoading &&
			isLocationWithContent &&
			!isAuth
		) {
			const fileName = locationPathname
				.split("/")
				.filter(Boolean)
				.join("-")
				.trim();
			dispatch(getUsersSeoContent({ fileName }));
		}
	}, [locationPathname, isAuth, seoContentLoaded, seoContentLoading]);

	const containerRef = useRef(null);
	const triggerObserver = useRef();
	const triggerRef = useCallback(
		(node) => {
			if (users.loading || !("IntersectionObserver" in window)) return;
			if (users.pagination.currentpage >= users.pagination.totalpages)
				return null;

			// ** Safe cleanup of the previous observer.
			if (triggerObserver.current) {
				try {
					triggerObserver.current.disconnect();
				} catch (error) {
					// ** Ignore errors during reconciliation.
				}
			}

			const marginValue = Math.min(
				containerRef.current?.getBoundingClientRect().height * 0.15 ||
					600,
				800,
			);

			triggerObserver.current = new IntersectionObserver(
				(entries) => {
					if (
						entries.some((entry) => entry.isIntersecting) &&
						!users.loading
					) {
						const apiParams = getApiParams({ isAuth });
						dispatch(
							getUsers({
								apiParams: {
									...apiParams,
									page: users.pagination.currentpage + 1,
								},
								isAuth,
								useUrl: true,
								locationMd5Hash: "",
								infinityScroll: true,
							}),
						).finally(() => {
							setIsChangingFilter(false);
						});
					}
				},
				{ rootMargin: `0px 0px ${marginValue}px 0px` },
			);

			// ** Safe observing the node.
			if (node && node.parentNode) {
				try {
					triggerObserver.current.observe(node);
				} catch (error) {
					// ** Ignore errors when the node is being removed.
				}
			}
		},
		[
			users.loading,
			users?.pagination?.currentpage,
			users?.pagination?.totalpages,
		],
	);

	// ** useEffect for cleanup IntersectionObserver, without it, there is probably a error "NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node".
	useEffect(() => {
		return () => {
			// ** Cleanup IntersectionObserver przy unmounting.
			if (triggerObserver.current) {
				try {
					triggerObserver.current.disconnect();
					triggerObserver.current = null;
				} catch (error) {
					// ** Ignore errors during reconciliation React.
				}
			}
		};
	}, []);

	const locationCity = useSelector(
		(state) => state.mainPageUsers.location.city,
	);
	const locationRegion = useSelector(
		(state) => state.mainPageUsers.location.region,
	);
	const locationCountry = useSelector(
		(state) => state.mainPageUsers.location.country,
	);
	const language = useSelector((state) => state.globalCommon.language);

	const headingText = useMemo(() => {
		if (!isAuth) {
			if (LOCATIONS_WITH_H1_LIKE_TITLE.includes(cityUrl)) {
				return `${title} - Strefa sex randek`;
			}
			if (preferencesUrl || preferenceUrl) {
				return t(`pages:users`);
			}
			return `Sex ${
				locationCity || locationRegion || locationCountry || ""
			}`;
		}
		return t(`pages:users`);
	}, [
		isAuth,
		locationCity,
		locationRegion,
		locationCountry,
		preferencesUrl,
		preferenceUrl,
		language,
	]);

	const heading2Text = useMemo(() => {
		const foundUsersCount = pseudoFormat(users.findnumber);

		return formatMessage(t(`common:metaDescription`), {
			foundUsersCount,
			locationCity,
		});
	}, [isAuth, locationCity, language]);

	const locationChipsVisible = !matchPath(locationPathname, {
		path: "/search/preferences/:preferences",
	});

	// ** Zapisanie obiektu query jako string
	const queryAsString = isAuth
		? Object.keys(query).length
			? queryString.stringify({ ...query, page: 1 })
			: queryString.stringify({ ...initialQuery })
		: "";

	const isLocationWithOtherFilters = useMemo(() => {
		return (
			countryUrl === "poland" &&
			cityUrl &&
			!LOCATIONS_WITHOUT_SEO_PREFERENCES_PAGES.includes(cityUrl)
		);
	}, [cityUrl, countryUrl]);

	const handleSetSelectedViewType = (newViewType) => {
		dispatch(setUsersListViewType(newViewType));
		history.push({
			pathname: location.pathname,
			search: generateQuery({
				...initialQuery,
				distance: isAuth ? initialQuery.distance : 0,
				...query,
				page: 1,
			}),
			state: {
				closeFiltersAfterLinkClick: true,
			},
		});
	};

	useEffect(() => {
		if (
			isAuth &&
			!query.sex &&
			userSearchSex &&
			Array.isArray(userSearchSex) &&
			userSearchSex.length > 0
		) {
			const to = generateQueryPath(location.pathname, {
				...initialQuery,
				distance: isAuth ? initialQuery.distance : 0,
				...query,
				page: 1,
				sex: userSearchSex?.join("-"),
			});
			history.replace(to);
		}
	}, []);

	// #####################################################

	// ** Przekierowanie na stronę błędu w przypadku nieprawidłowych parametrów w query
	if (
		(Object.keys(query).length > 0 &&
			(!includeAllQueryParams() || containInvalidQueryParam())) ||
		usersError ||
		containInvalidPreferenceParam()
	) {
		history.replace("/error404");
		return null;
	}

	return (
		<>
			{/* Sekcja HEAD */}
			<Helmet title={title}>
				<meta name="description" content={metaDescription} />
				{ogMetaTags}
				{canonicalLink()}
			</Helmet>

			<ScrollToTop />

			<CardsBlockWrapper>
				{/* Sekcja Filtrów */}
				<CardWrapper>
					<CardTitle>
						<Heading type="h1" data-testid="users-heading">
							{headingText}
						</Heading>
					</CardTitle>
					<CardBody>
						<Block flex withRowGap={"0.5rem"} column>
							{!isAuth && locationCity ? (
								<Heading type="h2" size="1.1rem">
									{heading2Text}
								</Heading>
							) : (
								<FiltersSectionWithHydrationOnDemand
									filtersOpen={filtersOpen}
									setFiltersOpen={setFiltersOpen}
									hideChangeFiltersButton={!isAuth}
								/>
							)}

							{!isAuth && (
								<>
									<BreadcrumbsWithHydrationOnDemand
										locationPath={match.path}
										locationChipsVisible={
											locationChipsVisible
										}
										preferenceUrl={
											preferenceUrl || preferencesUrl
										}
										preferenceQueryUrl={query.preferences}
										queryAsString={queryAsString}
										countryUrl={countryUrl}
										regionUrl={regionUrl}
										cityUrl={cityUrl}
									/>
									{isLocationWithOtherFilters && (
										<Block>
											<UsersSeoNavPanelTabsSelectors
												countryUrl={countryUrl}
												regionUrl={regionUrl}
												cityUrl={cityUrl}
												isMainListing
											/>
										</Block>
									)}
								</>
							)}
							{isAuth && (
								<Block>
									<NavPanelTabsSelectors
										viewType={viewType}
										handleSetSelectedViewType={
											handleSetSelectedViewType
										}
									/>
								</Block>
							)}
						</Block>
					</CardBody>
				</CardWrapper>

				{/* Główna zawartość strony */}
				<UsersList
					triggerRef={triggerRef}
					containerRef={containerRef}
					isChangingFilter={isChangingFilter}
					viewType={viewType}
					apiParams={getApiParams({ isAuth })}
					currentpage={users.pagination.currentpage}
					cityUrl={cityUrl}
				/>

				<EmptyBlock />
			</CardsBlockWrapper>
		</>
	);
};

// #####################################################

export default memo(Users, (prevProps, nextProps) => {
	return (
		prevProps.location.pathname === nextProps.location.pathname &&
		prevProps.location.search === nextProps.location.search &&
		JSON.stringify(prevProps.location.state) ===
			JSON.stringify(nextProps.location.state)
	);
});

// #####################################################
